import React, { useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { ArrowLeft2, ArrowRight2, Copy, ExportSquare, Notification, TickCircle } from 'iconsax-react';
import { copyCurrentUrl, getExternalBrowserUrl } from '../../lib/webPush';

/**
 * iOS 홈 화면 설치 안내 — 페이지형 하이퍼프레임
 *
 * 텍스트 리스트 대신, 유저가 실제로 보게 될 화면(Safari 하단 바 → 공유 시트 →
 * 홈 화면)을 실물 목업으로 한 장씩 보여준다. 탭/스와이프로 넘기고, 뒤로 갈 수
 * 있으며, 쪽수 점으로 진행이 보인다. 마지막 장의 CTA가 모달을 닫는다(onDone).
 *
 * needsSafari(인앱 브라우저·iOS Chrome)일 때는 상단에 원버튼 Safari 핸드오프:
 * 카카오톡 kakaotalk://web/openExternal · LINE openExternalBrowser=1 · x-safari- 스킴
 * + 링크 복사 폴백.
 */

// ---------------------------------------------------------------------------
// 스텝별 실물 목업 (전부 DOM/CSS — 이미지·영상 없음)
// ---------------------------------------------------------------------------

/** 순차 하이라이트 링 — phase A(0~50%)와 B(50~100%)가 번갈아 강조되는 루프 */
export const SeqRing: React.FC<{ reduce: boolean; phase: 'a' | 'b'; rounded?: string }> = ({ reduce, phase, rounded = 'rounded-xl' }) => (
    !reduce ? (
        <motion.span
            className={`pointer-events-none absolute inset-0 ${rounded} ring-2 ring-[#3182f6]`}
            animate={{ opacity: phase === 'a' ? [1, 1, 0, 0] : [0, 0, 1, 1] }}
            transition={{ duration: 3.6, times: [0, 0.45, 0.55, 1], repeat: Infinity, ease: 'easeInOut' }}
        />
    ) : (
        <span className={`pointer-events-none absolute inset-0 ${rounded} ring-2 ring-[#3182f6]/60`} />
    )
);

/** 1장: Safari 하단 바 — ⋯ 탭 → '공유' 선택이 한 장면에 */
const ShareStepVisual: React.FC<{ reduce: boolean }> = ({ reduce }) => (
    <div className="relative flex w-full max-w-[260px] flex-col items-end gap-2">
        {/* ⋯ 메뉴 팝업 */}
        <motion.div
            initial={reduce ? undefined : { opacity: 0, y: 10, scale: 0.95 }}
            animate={reduce ? undefined : { opacity: 1, y: 0, scale: 1 }}
            transition={{ delay: 0.45, type: 'spring', stiffness: 300, damping: 24 }}
            className="w-[160px] overflow-hidden rounded-2xl border border-[#3a3d44] bg-[#26282e] text-[12px] shadow-[0_16px_40px_rgba(0,0,0,0.7)]"
        >
            <div className="relative flex items-center justify-between px-3.5 py-2.5 text-white">
                <span className="font-medium">공유</span>
                <ExportSquare size={14} variant="Linear" style={{ color: '#ffffff' }} />
                <SeqRing reduce={reduce} phase="b" rounded="rounded-none" />
            </div>
            <div className="flex items-center justify-between border-t border-[#3a3d44] px-3.5 py-2.5 text-[#8a8f98]">
                북마크에 추가
                <span className="text-[13px]">◻</span>
            </div>
        </motion.div>

        {/* Safari 하단 바 */}
        <div className="flex w-full items-center gap-1.5 rounded-2xl border border-[#2b2e34] bg-[#1b1d22] px-2.5 py-2 text-[11px] text-[#8a8f98] shadow-[0_8px_24px_rgba(0,0,0,0.5)]">
            <span className="px-1 text-[13px]">〈</span>
            <span className="flex flex-1 items-center justify-center gap-1.5 rounded-xl border border-[#2b2e34] bg-[#101216] px-2 py-2">
                <span className="text-[9px]">🔒</span> socialdataai.com
            </span>
            <span className="relative rounded-xl px-2.5 py-1.5 font-bold text-white">
                ⋯
                <SeqRing reduce={reduce} phase="a" />
                {!reduce && (
                    <motion.span
                        className="pointer-events-none absolute left-1/2 top-1/2 h-8 w-8 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white/25"
                        animate={{ scale: [0, 1.4], opacity: [0.7, 0] }}
                        transition={{ duration: 1.8, repeat: Infinity, ease: 'easeOut' }}
                    />
                )}
            </span>
        </div>
        <p className="w-full text-center text-[10px] text-[#777777]">
            하단에 공유 아이콘(네모+화살표)이 바로 보이면 그걸 눌러도 돼요
        </p>
    </div>
);

/** 2장: 공유 시트 — '더 보기'를 거쳐 '홈 화면에 추가'까지, 실물 구조 그대로 */
const AddStepVisual: React.FC<{ reduce: boolean }> = ({ reduce }) => (
    <div className="w-full max-w-[260px] overflow-hidden rounded-2xl border border-[#3a3d44] bg-[#26282e] text-[12px] shadow-[0_16px_40px_rgba(0,0,0,0.7)]">
        {/* 시트 헤더: 사이트 정보 */}
        <div className="flex items-center gap-2 border-b border-[#3a3d44] px-3 py-2">
            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-white text-[7px] font-bold text-black">
                SDL
            </span>
            <span className="min-w-0">
                <span className="block truncate text-[10px] font-medium text-white">소셜데이터랩 - Instagram 방문…</span>
                <span className="block text-[9px] text-[#8a8f98]">socialdataai.com</span>
            </span>
        </div>

        {/* 원형 액션 행 — 바로 안 보이면 '더 보기' */}
        <div className="flex items-start justify-between border-b border-[#3a3d44] px-3.5 py-2">
            {[['⧉', '복사'], ['⌗', '북마크'], ['👓', '읽기 목록']].map(([glyph, label]) => (
                <span key={label} className="flex flex-col items-center gap-1 text-[8px] text-[#8a8f98]">
                    <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#33363c] text-[12px] text-white/80">{glyph}</span>
                    {label}
                </span>
            ))}
            <span className="relative flex flex-col items-center gap-1 text-[8px] text-white">
                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#33363c] text-[12px] text-white">⌄</span>
                더 보기
                <SeqRing reduce={reduce} phase="a" rounded="rounded-lg" />
            </span>
        </div>

        {/* 펼쳐진 목록 */}
        <div className="px-0">
            <div className="px-3.5 py-2 text-[#8a8f98]">즐겨찾기에 추가</div>
            <div className="relative border-t border-[#3a3d44] px-3.5 py-2.5 text-white">
                <span className="flex items-center justify-between">
                    <span className="font-semibold">홈 화면에 추가</span>
                    <span className="flex h-4 w-4 items-center justify-center rounded border border-white/70 text-[10px] leading-none">+</span>
                </span>
                <SeqRing reduce={reduce} phase="b" rounded="rounded-none" />
            </div>
            <div className="border-t border-[#3a3d44] px-3.5 py-2 text-[#8a8f98]">마크업</div>
        </div>
    </div>
);

/** 3장: 홈 화면의 설치된 앱 + 실시간 알림 도착 장면 (무료 강조) */
const OpenStepVisual: React.FC<{ reduce: boolean }> = ({ reduce }) => (
    <div className="flex w-full max-w-[260px] flex-col items-center gap-2.5">
        <div className="relative w-full overflow-hidden rounded-2xl border border-[#262626] bg-gradient-to-b from-[#10141c] to-[#0b0d12] px-4 pb-3 pt-9">
            {/* 실시간 알림 배너가 위에서 도착 */}
            <motion.div
                initial={reduce ? undefined : { y: -36, opacity: 0 }}
                animate={reduce ? undefined : { y: [-36, 0, 0, 0, -36], opacity: [0, 1, 1, 1, 0] }}
                transition={{ duration: 4.2, times: [0, 0.15, 0.5, 0.85, 1], repeat: Infinity, ease: 'easeInOut', delay: 0.6 }}
                className="absolute inset-x-2 top-1.5 flex items-center gap-1.5 rounded-[10px] border border-white/[0.08] bg-[#2a2f38]/95 px-2 py-1.5 shadow-lg"
            >
                <span className="flex h-4 w-4 shrink-0 items-center justify-center rounded bg-[#3182f6]">
                    <Notification size={10} variant="Bold" style={{ color: '#ffffff' }} />
                </span>
                <span className="min-w-0">
                    <span className="block truncate text-[8px] font-bold text-white">소셜데이터랩 · 지금</span>
                    <span className="block truncate text-[8.5px] text-white/80">방금 프로필 방문이 감지되었습니다</span>
                </span>
            </motion.div>

            {/* 홈 화면 그리드 */}
            <div className="grid grid-cols-4 gap-3">
                {Array.from({ length: 3 }, (_, i) => (
                    <span key={i} className="flex flex-col items-center gap-1">
                        <span className="h-10 w-10 rounded-[11px] bg-[#22262d]" />
                        <span className="h-1.5 w-8 rounded bg-[#22262d]" />
                    </span>
                ))}
                <span className="relative flex flex-col items-center gap-1">
                    <motion.span
                        initial={reduce ? undefined : { scale: 0, opacity: 0 }}
                        animate={reduce ? undefined : { scale: 1, opacity: 1 }}
                        transition={{ delay: 0.3, type: 'spring', stiffness: 260, damping: 16 }}
                        className="flex h-10 w-10 items-center justify-center rounded-[11px] bg-gradient-to-br from-[#0d0d0d] to-[#1c2230] ring-1 ring-[#3182f6]/70"
                    >
                        <Notification size={18} variant="Bold" style={{ color: '#3182f6' }} />
                    </motion.span>
                    <span className="max-w-[44px] truncate text-[7px] text-white/80">소셜데이터랩</span>
                    {!reduce && (
                        <motion.span
                            className="pointer-events-none absolute left-1/2 top-5 h-10 w-10 -translate-x-1/2 -translate-y-1/2 rounded-[11px] ring-2 ring-[#3182f6]"
                            animate={{ opacity: [0.8, 0, 0.8], scale: [1, 1.3, 1] }}
                            transition={{ delay: 0.8, duration: 1.8, repeat: Infinity, ease: 'easeInOut' }}
                        />
                    )}
                </span>
            </div>
        </div>
        <p className="text-center text-[11px] leading-relaxed text-[#A8A8A8]">
            홈 화면에 생긴 <span className="font-semibold text-white">소셜데이터랩</span> 앱을 열고{' '}
            <span className="font-semibold text-white">'알림 켜기' 한 번만 더</span> 누르면<br />
            <span className="font-semibold text-[#3182f6]">실시간 푸시 알림을 무료로</span> 받을 수 있어요
        </p>
    </div>
);

// ---------------------------------------------------------------------------
// 페이지 정의 + 페이저
// ---------------------------------------------------------------------------

const PAGES = [
    { key: 'share', title: '공유 메뉴를 열어주세요', Visual: ShareStepVisual },
    { key: 'add', title: "'홈 화면에 추가'를 선택해주세요", Visual: AddStepVisual },
    { key: 'open', title: '홈 화면에서 열어주세요', Visual: OpenStepVisual },
] as const;

/**
 * 인앱/타 브라우저 → 기본 브라우저 원버튼 핸드오프 (모달의 'external' 스텝에서도 재사용).
 * 자동 이동은 스킴 특성상 실패할 수 있어(특히 인스타그램 인앱) 시도 후 폴백 안내를 띄운다.
 */
export const ExternalBrowserHandoff: React.FC<{ browserName?: string }> = ({ browserName = 'Safari' }) => {
    const [copied, setCopied] = useState(false);
    const [attempted, setAttempted] = useState(false);

    const handleOpenExternal = () => {
        const target = getExternalBrowserUrl();
        if (!target) return;
        setAttempted(true);
        window.location.href = target;
    };

    const handleCopy = async () => {
        const ok = await copyCurrentUrl();
        setCopied(ok);
        if (ok) window.setTimeout(() => setCopied(false), 4000);
    };

    return (
        <div className="space-y-2">
            <div className="rounded-xl border border-[#FFB800]/25 bg-[#FFB800]/10 px-3.5 py-2.5 text-xs leading-relaxed text-[#FFB800]">
                이 브라우저에서는 알림 설정이 안 돼요 —{' '}
                <span className="text-white">아래 버튼으로 {browserName}에서 이어서 하면 돼요.</span>
            </div>
            <div className="flex gap-2">
                <motion.button
                    whileTap={{ scale: 0.98 }}
                    onClick={handleOpenExternal}
                    className="flex-1 rounded-xl bg-[#3182f6] py-3 text-sm font-semibold text-white transition-colors hover:bg-[#2b74dc]"
                >
                    {browserName}로 자동 이동
                </motion.button>
                <motion.button
                    whileTap={{ scale: 0.98 }}
                    onClick={handleCopy}
                    className={`inline-flex items-center justify-center gap-1.5 rounded-xl border px-4 py-3 text-sm font-medium transition-colors ${
                        copied
                            ? 'border-[#00D87E]/40 text-[#00D87E]'
                            : 'border-[#262626] text-[#A8A8A8] hover:text-white'
                    }`}
                >
                    {copied ? (
                        <>
                            <TickCircle size={16} variant="Bold" style={{ color: '#00D87E' }} />
                            복사됐어요
                        </>
                    ) : (
                        <>
                            <Copy size={16} variant="Linear" style={{ color: 'currentColor' }} />
                            링크 복사
                        </>
                    )}
                </motion.button>
            </div>
            {attempted && (
                <p className="text-center text-[11px] leading-relaxed text-[#777777]">
                    자동 이동이 안 되면 링크를 복사해서 {browserName}에 붙여넣어 주세요
                </p>
            )}
        </div>
    );
};

interface IosInstallGuideProps {
    needsSafari?: boolean;
    /** 마지막 장 '확인했어요' — 보통 모달 닫기 */
    onDone?: () => void;
}

export const IosInstallGuide: React.FC<IosInstallGuideProps> = ({ needsSafari = false, onDone }) => {
    const reduce = useReducedMotion() ?? false;
    const [[page, direction], setPageState] = useState<[number, number]>([0, 0]);
    // 스와이프 직후 살아남는 잔여 click이 스테이지의 '탭=다음'을 오발동시키지 않게 하는 가드
    const lastDragEndAt = React.useRef(0);

    const paginate = (dir: number) => {
        setPageState(([current]) => {
            const next = Math.min(Math.max(current + dir, 0), PAGES.length - 1);
            return [next, dir];
        });
    };

    const isLast = page === PAGES.length - 1;
    const { title, Visual } = PAGES[page];

    return (
        <div className="space-y-3">
            {needsSafari && <ExternalBrowserHandoff />}

            {/* 페이지 스테이지 — 탭하면 다음, 스와이프 지원 */}
            <div
                className="relative h-[268px] overflow-hidden rounded-2xl border border-[#262626] bg-[#0F0F0F]"
                onClick={() => {
                    if (Date.now() - lastDragEndAt.current < 350) return; // 스와이프 잔여 클릭 무시
                    if (!isLast) paginate(1);
                }}
            >
                <span className="absolute left-3 top-3 z-10 rounded-full border border-[#262626] bg-[#181818] px-2 py-0.5 text-[10px] font-bold text-[#A8A8A8]">
                    {page + 1} / {PAGES.length}
                </span>

                {/* 중첩 AnimatePresence 금지: 부모(모달) 퇴장을 영구 블로킹하는 framer 결함이 있어
                    key 교체 + 진입 애니메이션만 사용한다 (이전 장은 즉시 교체, 새 장이 슬라이드 인) */}
                <motion.div
                    key={page}
                    initial={reduce ? false : { x: direction >= 0 ? 260 : -260, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={reduce ? { duration: 0 } : { type: 'spring', stiffness: 320, damping: 32 }}
                    drag={reduce ? false : 'x'}
                    dragConstraints={{ left: 0, right: 0 }}
                    dragElastic={0.5}
                    onDragEnd={(_, info) => {
                        lastDragEndAt.current = Date.now();
                        if (info.offset.x < -60) paginate(1);
                        else if (info.offset.x > 60) paginate(-1);
                    }}
                    className="absolute inset-0 flex flex-col items-center justify-center gap-3 px-5 pt-6"
                >
                    <Visual reduce={reduce} />
                    <p className="text-center text-sm font-semibold text-white">{title}</p>
                </motion.div>
            </div>

            {/* 컨트롤: 뒤로 · 쪽수 점 · 다음/완료 */}
            <div className="flex items-center gap-2">
                <motion.button
                    whileTap={{ scale: 0.95 }}
                    onClick={() => paginate(-1)}
                    disabled={page === 0}
                    aria-label="이전 단계"
                    className="flex h-11 w-11 items-center justify-center rounded-xl border border-[#262626] text-[#A8A8A8] transition-colors hover:text-white disabled:opacity-30"
                >
                    <ArrowLeft2 size={18} variant="Linear" style={{ color: 'currentColor' }} />
                </motion.button>

                <div className="flex flex-1 items-center justify-center gap-1.5">
                    {PAGES.map((p, index) => (
                        <button
                            key={p.key}
                            onClick={() => setPageState(([current]) => [index, index > current ? 1 : -1])}
                            aria-label={`${index + 1}번째 단계로 이동`}
                            className={`h-1.5 rounded-full transition-all duration-300 ${
                                index === page ? 'w-5 bg-[#3182f6]' : 'w-1.5 bg-[#363636]'
                            }`}
                        />
                    ))}
                </div>

                <motion.button
                    whileTap={{ scale: 0.97 }}
                    onClick={() => (isLast ? onDone?.() : paginate(1))}
                    className={`inline-flex h-11 items-center justify-center gap-1 rounded-xl px-5 text-sm font-semibold transition-colors ${
                        isLast
                            ? 'border border-[#262626] text-[#A8A8A8] hover:text-white'
                            : 'bg-[#3182f6] text-white hover:bg-[#2b74dc]'
                    }`}
                >
                    {isLast ? '확인했어요' : '다음'}
                    {!isLast && <ArrowRight2 size={16} variant="Linear" style={{ color: '#ffffff' }} />}
                </motion.button>
            </div>

            <p className="text-center text-[11px] leading-relaxed text-[#777777]">
                iPhone은 홈 화면에 설치된 앱에서만 실시간 알림을 받을 수 있어요 (iOS 16.4 이상)
            </p>
        </div>
    );
};

export default IosInstallGuide;
